import { ChangeEvent, useEffect, useMemo, useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { Grid, Typography } from '@material-ui/core'
import { useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'

import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useGetKnowledgeBaseEntityQuery } from 'api/queries/knowledgeBase/knowledgeBase'
import { useKnowledgeBaseContentData } from 'hooks/useKnowledgeBaseContentData'
import {
  useMoveKnowledgeBaseEntityMutation,
  useDeleteKnowledgeBaseObjectMutation,
  useEditKnowledgeBaseObjectMutation
} from 'api/mutations/knowledgeBase/knowledgeBase'
import { useCopyQuizMutation } from 'api/mutations/quizes/quizes'
import { KNOWLEDGE_BASE_COMPANY_ID_TOP_QUERY_KEY, KNOWLEDGE_BASE_CONTENT_QUERY_KEY, KNOWLEDGE_BASE_ENTITY_QUERY_KEY, KNOWLEDGE_BASE_OWN_COMPANY_TOP_QUERY_KEY } from 'api/queriesKeys'
import { useCompanyId } from 'providers/CompanyProvider'
import { useRedirectOnCompanyChange } from 'hooks/useRedirectOnCompanyChange'
import { KnowledgeBaseBreadcrumbs } from 'components/navigation/KnowledgeBaseBreadcrumbs/KnowledgeBaseBreadcrumbs'
import { CardsList } from 'components/data/CardsList/CardsList'
import { SearchFilterInput } from 'components/inputs/SearchFilterInput/SearchFilterInput'
import { SortSelect } from 'components/data/SortSelect/SortSelect'
import { GroupSelect } from 'components/data/GroupSelect/GroupSelect'
import { MoveElementModal } from 'components/modals/MoveElementModal/MoveElementModal'
import { GroupModal } from 'components/modals/GroupModal/GroupModal'
import { ConfirmModal } from 'components/modals/ConfirmModal/ConfirmModal'
import { DeleteModal } from 'components/modals/DeleteModal/DeleteModal'
import { OLDEST } from 'constants/sortTypes'
import { KnowledgeBaseCreateSection } from 'components/data/KnowledgeBase/KnowledgeBaseCreateSection'
import { SortType } from 'types/SortType'
import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { useKnowledgeBaseFormImage } from 'hooks/useKnowledgeBaseFormImage'
import { Translation } from 'types/Translation'
import { LabelSelect } from 'components/data/LabelSelect/LabelSelect'
import { GroupFormFields } from 'components/forms/GroupForm/types'
import { KnowledgeBaseContentModal } from 'components/modals/KnowledgeBaseContentModal/KnowledgeBaseContentModal'
import { KnowledgeBaseContentFormFields } from 'components/forms/KnowledgeBaseContentForm/KnowledgeBaseContentForm'
import { getFormInitValues, parseEditContentFields } from 'screens/KnowledgeBase/KnowledgeBaseContentEditScreen/KnowledgeBaseContentEditScreen.utils'
import { useDestination } from 'hooks/useDestination'
import { Label } from 'types/Label'
import { useHasRole } from 'hooks/useHasRoles'
import { ADMIN_ROLE } from 'constants/roleGroups'
import * as Styled from './KnowledgeBaseScreen.styles'
import { mapKnowledgeBasePages } from './KnowledgeBaseScreen.utils'

interface RouteParams {
  id: string
}

const KnowledgeBaseScreen = () => {
  const { t } = useTranslation()
  const { id } = useParams<RouteParams>()
  const queryClient = useQueryClient()
  const companyId = useCompanyId() || 0
  useRedirectOnCompanyChange('/knowledge-base')
  const isAdmin = useHasRole(ADMIN_ROLE)

  const [
    filterGroupType,
    setFilterGroupType
  ] = useState<KnowledgeBaseContentType>(KnowledgeBaseContentType.ALL)
  const [
    filterLabels,
    setFilterLabels
  ] = useState<number[]>([])
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [sortType, setSortType] = useState<SortType>(OLDEST)
  const [isEditGroupModalOpen, setEditGroupModalOpen] = useState<boolean>(false)
  const [
    isEditKnowledgeBaseContentModalOpen,
    setEditKnowledgeBaseContentModalOpen
  ] = useState<boolean>(false)
  const [isMoveModalOpen, setMoveModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [isCopyModalOpen, setCopyModalOpen] = useState<boolean>(false)
  const [changedElementId, setChangedElementId] = useState<number>(0)
  const [changedGroupName, setChangedGroupName] = useState<string>('')
  const [changedGroupImage, setChangedGroupImage] = useState<File | string | null>(null)
  const [changedGroupNameTranslations, setChangedGroupNameTranslations] = useState<Translation[]>(
    []
  )
  const [changedGroupLabels, setChangedGroupLabels] = useState<Label[]>([])
  const [loaderElement, setLoaderElement] = useState<HTMLDivElement | null>(null)

  const loader = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setLoaderElement(node)
    }
  }, [])

  const {
    data: knowledgeBaseEntityData,
    isLoading: isLoadingEntity,
    isError: isErrorEntity,
  } = useGetKnowledgeBaseEntityQuery(+id, { enabled: !!id })

  const {
    data: knowledgeBaseContentData,
    isLoading: isLoadingContent,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useKnowledgeBaseContentData(
    companyId,
    filterGroupType,
    sortType,
    searchFilter,
    id,
    filterLabels
  )

  const invalidateData = async () => {
    await queryClient.invalidateQueries(KNOWLEDGE_BASE_ENTITY_QUERY_KEY)
    await queryClient.invalidateQueries(KNOWLEDGE_BASE_CONTENT_QUERY_KEY)
    await queryClient.invalidateQueries(
      companyId ? KNOWLEDGE_BASE_COMPANY_ID_TOP_QUERY_KEY : KNOWLEDGE_BASE_OWN_COMPANY_TOP_QUERY_KEY
    )
  }

  const { mutate: moveItem, isLoading: isMoveLoading } = useMoveKnowledgeBaseEntityMutation({
    onSuccess: async () => {
      await invalidateData()
      setMoveModalOpen(false)
    },
  })

  const { mutate: editGroup, isLoading: isEditGroupLoading } = useEditKnowledgeBaseObjectMutation()

  const {
    data: editedKnowledgeBaseData,
    isLoading: isEditedKnowledgeBaseLoading,
    isError: isEditedKnowledgeBaseError,
  } = useGetKnowledgeBaseEntityQuery(+changedElementId, {
    refetchOnWindowFocus: false,
    enabled: !!changedElementId && isEditKnowledgeBaseContentModalOpen,
  })

  const {
    mutate: editObjectMutate,
    isLoading: isEditKnowledgeBaseContentLoading,
  } = useEditKnowledgeBaseObjectMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries([KNOWLEDGE_BASE_ENTITY_QUERY_KEY, { changedElementId }])
      await invalidateData()
      setEditKnowledgeBaseContentModalOpen(false)
    },
  })

  const destinationText = useDestination(editedKnowledgeBaseData)

  const editKnowledgeBaseContentFormInitValues = useMemo(() => (
    editedKnowledgeBaseData ? getFormInitValues(editedKnowledgeBaseData) : undefined
  ), [editedKnowledgeBaseData])

  const {
    isAddImageMutationLoading: isAddGroupImageMutationLoading,
    isDeleteImageMutationLoading: isDeleteGroupImageMutationLoading,
    handleSubmitImage: handleSubmitGroupImage
  } = useKnowledgeBaseFormImage({
    groupId: +id || undefined,
  })

  const {
    isAddImageMutationLoading: isAddContentImageMutationLoading,
    isDeleteImageMutationLoading: isDeleteContentImageMutationLoading,
    handleSubmitImage: handleSubmitContentImage
  } = useKnowledgeBaseFormImage({
    groupId: +changedElementId || undefined,
  })

  const {
    mutate: deleteItem,
    isLoading: isDeleteLoading,
    isError: isDeleteError,
    reset: deleteReset
  } = useDeleteKnowledgeBaseObjectMutation({
    onSuccess: async () => {
      setDeleteModalOpen(false)
      await invalidateData()
      setSearchFilter('')
    }
  })

  const {
    mutate: copyQuiz,
    isLoading: isCopyLoading
  } = useCopyQuizMutation({
    onSuccess: async () => {
      setCopyModalOpen(false)
      await invalidateData()
    }
  })

  const isIntersecting = useIntersectionObserver(loaderElement)
  const isLoading = isLoadingEntity || isLoadingContent

  const knowledgeBaseItems = useMemo(() => {
    if (knowledgeBaseContentData?.pages) {
      const { pages } = knowledgeBaseContentData

      return mapKnowledgeBasePages(pages).reduce((prev, cur) => [...prev, ...cur])
    }
    return []
  }, [knowledgeBaseContentData])

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [isIntersecting, hasNextPage, fetchNextPage, knowledgeBaseContentData, isFetching])

  const handleSaveContent = (destinationId?: number | null) => {
    moveItem({
      newParentId: destinationId,
      objectIds: [changedElementId]
    })
  }

  const handleSaveEditGroup = (values: GroupFormFields) => {
    editGroup({
      id: changedElementId,
      dto: {
        dataType: KnowledgeBaseContentType.GROUP,
        name: values.name,
        nameTranslations: values.nameTranslations,
        parentId: id,
        languageCode: null,
        labelIds: values.labelIds,
      }
    },
    {
      onSuccess: async (data) => {
        await handleSubmitGroupImage(values.image, data.id)
        await invalidateData()
        setEditGroupModalOpen(false)
      }
    })
  }

  const handleSortSelectChange = (event: ChangeEvent<{
    name?: string | undefined
    value: unknown
  }>) => {
    setSortType(event.target.value as SortType)
  }

  const handleGroupSelectChange = (event: ChangeEvent<{
    name?: string | undefined
    value: unknown
  }>) => {
    setFilterGroupType(event.target.value as KnowledgeBaseContentType)
  }

  const handleLabelSelectChange = (event: ChangeEvent<{
    name?: string | undefined
    value: unknown
  }>) => {
    const labelIds = event.target.value as number[]
    setFilterLabels(labelIds)
  }

  const handleEditGroup = (
    elementId: number,
    name: string,
    nameTranslations: Translation[],
    image: File | string | null,
    labels: Label[],
    dataType?: string,
  ) => {
    if (dataType === KnowledgeBaseContentType.GROUP) {
      setChangedGroupName(name)
      setChangedElementId(elementId)
      setChangedGroupImage(image)
      setChangedGroupNameTranslations(nameTranslations)
      setChangedGroupLabels(labels)
      setEditGroupModalOpen(true)
    } else {
      setChangedElementId(elementId)
      setEditKnowledgeBaseContentModalOpen(true)
    }
  }

  const handleSaveEditKnowledgeBaseContent = async (
    values: KnowledgeBaseContentFormFields,
    languageCode: string
  ) => {
    const { image } = values
    await handleSubmitContentImage(image, +changedElementId)

    editObjectMutate({
      id: +changedElementId,
      dto: parseEditContentFields(values, languageCode, isAdmin),
    })
  }

  const handleMove = (elementId: React.SetStateAction<number>) => {
    setMoveModalOpen(true)
    setChangedElementId(elementId)
  }

  const onAddSuccess = async () => {
    await invalidateData()
    setSearchFilter('')
  }

  return (
    <Styled.Container>
      <Grid container item xs={12} spacing={2}>
        <Styled.SearchElement item xs={2} $maxWidth='20%' $grow>
          <GroupSelect
            value={filterGroupType}
            onChange={handleGroupSelectChange}
            without={[KnowledgeBaseContentType.GROUP]}
          />
        </Styled.SearchElement>
        <Styled.SearchElement item xs={2} $maxWidth='20%' $grow>
          <LabelSelect
            inputLabel={t('common.labels')}
            value={filterLabels}
            onChange={handleLabelSelectChange}
            multiple
          />
        </Styled.SearchElement>
        <Styled.SearchElement item xs={2} $maxWidth='20%' $grow>
          <SortSelect
            withoutNone
            value={sortType}
            onChange={handleSortSelectChange}
          />
        </Styled.SearchElement>
        <Styled.SearchElement item xs={4} $maxWidth='40%' $grow>
          <SearchFilterInput
            label={t('common.filter')}
            placeholder={t('common.startWrite')}
            searchFilterValue={searchFilter}
            onChangeSearchFilter={setSearchFilter}
          />
        </Styled.SearchElement>
      </Grid>
      {isLoading ? (
        <Styled.CircularProgress color='primary' />
      ) : (
        <>
          <Grid item container xs={12} justifyContent='space-between' alignItems='center'>
            <Grid item>
              <KnowledgeBaseBreadcrumbs
                knowledgeBaseEntity={knowledgeBaseEntityData}
                isError={isErrorEntity}
              />
            </Grid>
            <KnowledgeBaseCreateSection
              onSuccess={onAddSuccess}
              kbEntityId={knowledgeBaseEntityData?.id}
            />
          </Grid>
          <>
            <CardsList
              data={knowledgeBaseItems}
              emptyListInfoText={t('knowledgeBase.empty')}
              onMove={(elementId) => handleMove(elementId)}
              onEdit={(elementId, name, nameTranslations, image, labels, dataType) =>
                handleEditGroup(elementId, name, nameTranslations, image, labels, dataType)}
              onDelete={(elementId) => {
                setDeleteModalOpen(true)
                setChangedElementId(elementId)
              }}
              onCopy={(elementId) => {
                setCopyModalOpen(true)
                setChangedElementId(elementId)
              }}
            />
            <Styled.LoadMoreContainer ref={loader}>
              {isFetchingNextPage && <Typography>{t('common.loading')}...</Typography>}
            </Styled.LoadMoreContainer>
          </>
        </>
      )}
      <MoveElementModal
        open={isMoveModalOpen}
        movedElementId={changedElementId}
        onClose={() => setMoveModalOpen(false)}
        onSave={(desiredId) => handleSaveContent(desiredId)}
        isLoading={isMoveLoading}
      />
      <GroupModal
        open={isEditGroupModalOpen}
        isEdit
        initialValues={{
          name: changedGroupName,
          image: changedGroupImage,
          nameTranslations: changedGroupNameTranslations,
          labelIds: changedGroupLabels.map(({ id: labelId }) => labelId),
        }}
        onClose={() => setEditGroupModalOpen(false)}
        onSave={handleSaveEditGroup}
        isLoading={
          isEditGroupLoading || isAddGroupImageMutationLoading || isDeleteGroupImageMutationLoading
        }
      />
      <DeleteModal
        open={isDeleteModalOpen}
        subText={t('knowledgeBase.delete.groupConfirmSubText')}
        onClose={() => {
          setDeleteModalOpen(false)
          deleteReset()
        }}
        onSave={() => deleteItem(changedElementId)}
        errorText={isDeleteError ? t('knowledgeBase.delete.error') : ''}
        isLoading={isDeleteLoading}
      />
      <ConfirmModal
        open={isCopyModalOpen}
        title={t('knowledgeBase.copy.confirmTitle')}
        subText={t('knowledgeBase.copy.quizCopySubText')}
        onClose={() => setCopyModalOpen(false)}
        onSave={() => copyQuiz(changedElementId)}
        isLoading={isCopyLoading}
        confirmLabel={t('common.copy')}
      />
      <KnowledgeBaseContentModal
        open={isEditKnowledgeBaseContentModalOpen}
        isEdit
        initialValues={editKnowledgeBaseContentFormInitValues}
        destinationPlaceholder={destinationText}
        onSave={handleSaveEditKnowledgeBaseContent}
        isLoading={
          isEditKnowledgeBaseContentLoading
          || isAddContentImageMutationLoading
          || isDeleteContentImageMutationLoading
        }
        onClose={() => setEditKnowledgeBaseContentModalOpen(false)}
        isDataLoading={isEditedKnowledgeBaseLoading}
        isDataError={isEditedKnowledgeBaseError}
      />
    </Styled.Container>
  )
}

export default KnowledgeBaseScreen
